import { useCallback, useState } from 'react';

import {
  fetchRobloxGameRanks,
  parseRobloxGameRankResponse,
} from '@intelligence/v3/services/robloxGameRank';
import { buildRobloxGameRankChartFetchParams } from '../utils/buildRobloxGameRankChartFetchParams';

import type { IMainGenre } from '@intelligence/types/common';
import type { IRobloxResolvedDateRange } from '@intelligence/v3/utils/robloxFilterDate';
import type { IRobloxGameRankFilterValues, IRobloxGameRankRow } from '../types';

interface IFetchRobloxGameRankChartOptions {
  source: string;
  gameIds: string[];
  resolvedDateRange?: IRobloxResolvedDateRange | null;
}

export default function useRobloxGameRankChart(
  robloxGenreOptionsRef?: React.MutableRefObject<IMainGenre[]>,
) {
  const [loading, setLoading] = useState(false);
  const [chartList, setChartList] = useState<IRobloxGameRankRow[]>([]);

  const fetchChart = useCallback(async (
    filterValues: IRobloxGameRankFilterValues,
    options: IFetchRobloxGameRankChartOptions,
  ) => {
    const gameIds = options.gameIds.filter(Boolean);

    if (!gameIds.length) {
      setChartList([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const params = buildRobloxGameRankChartFetchParams(filterValues, {
        source: options.source,
        gameIds,
        robloxGenreOptions: robloxGenreOptionsRef?.current,
        resolvedDateRange: options.resolvedDateRange,
      });
      const response = await fetchRobloxGameRanks(params);
      const { list } = parseRobloxGameRankResponse(response);
      setChartList(list);
    } catch (error) {
      console.debug('[RobloxGameRank] fetch chart failed', error);
      setChartList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearChart = useCallback(() => {
    setChartList([]);
    setLoading(false);
  }, []);

  const setChartLoading = useCallback((nextLoading: boolean) => {
    setLoading(nextLoading);
  }, []);

  return {
    loading,
    chartList,
    fetchChart,
    clearChart,
    setChartLoading,
  };
}
